/**
 * Dynamic-content insertions are stitched into the assembled audio once per
 * episode. Pro publishes them as {at_ms, duration_ms}, with at_ms on the
 * ORIGINAL timeline: the timeline of the source audio and, when
 * timeline.video_matches is true, of the video. These two functions are the
 * only place the player converts between the original timeline and the
 * assembled-audio timeline. Call sites use seconds; the maths runs in whole
 * milliseconds, exactly like Pro's TimelineShiftService.
 */
function normalized(insertions) {
    if (!Array.isArray(insertions)) {
        return []
    }

    return insertions
        .filter((insertion) => Number.isFinite(insertion?.at_ms) && Number.isFinite(insertion?.duration_ms) && insertion.duration_ms > 0)
        .slice()
        .sort((a, b) => a.at_ms - b.at_ms)
}

/**
 * Original → assembled audio: t + the durations of every insertion with
 * at_ms <= t. Mirrors Pro's shift(), boundary rule included.
 */
export function toAudio(seconds, insertions = []) {
    if (!Number.isFinite(seconds)) {
        return seconds
    }

    const ms = Math.round(seconds * 1000)
    let shiftMs = 0

    for (const insertion of normalized(insertions)) {
        if (insertion.at_ms > ms) {
            break
        }

        shiftMs += insertion.duration_ms
    }

    return (ms + shiftMs) / 1000
}

/**
 * Assembled audio → original. A time inside an insertion maps to the
 * insertion's original position; any other time loses the durations of the
 * insertions that end at or before it.
 */
export function toOriginal(seconds, insertions = []) {
    if (!Number.isFinite(seconds)) {
        return seconds
    }

    const ms = Math.round(seconds * 1000)
    let removedMs = 0

    for (const insertion of normalized(insertions)) {
        const assembledStart = insertion.at_ms + removedMs
        const assembledEnd = assembledStart + insertion.duration_ms

        if (ms < assembledStart) {
            break
        }

        if (ms < assembledEnd) {
            return insertion.at_ms / 1000
        }

        removedMs += insertion.duration_ms
    }

    return (ms - removedMs) / 1000
}
